import { Transition } from "@headlessui/react"
import {
  DetailedHTMLProps,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { IoAlertCircle, IoCheckmarkCircle } from "react-icons/io5"
import {
  DraggableDirection,
  Id,
  Theme,
  toast as toastBase,
  ToastContent,
  ToastOptions,
  ToastPosition,
  UpdateOptions,
} from "react-toastify"

const ToolTip: FC<{
  toolTip: ToolTip
  orientationInstruction: OrientationInstruction
}> = ({ toolTip, orientationInstruction }) => {
  const dynamicToolTipCss = useMemo(() => {
    return setStyleType(orientationInstruction)
  }, [orientationInstruction])

  switch (toolTip.state) {
    case ToolTipType.Headless: {
      return toolTip.value
    }
    case ToolTipType.Standard:
    case ToolTipType.Warn:
    case ToolTipType.Alert: {
      const width = toolTip.width
      const height = toolTip.height

      return (
        <div
          className={`absolute z-[100] mb-2 px-[8px] py-[4px] flex rounded-md shadow-elevation-max ${
            toolTip.state === ToolTipType.Alert || toolTip.state === ToolTipType.Warn
              ? toolTip.state === ToolTipType.Alert
                ? "text-alert-primary"
                : "text-warn-primary"
              : ""
          }`}
          style={{
            ...(width ? { width } : { whiteSpace: "nowrap" }),
            height,
            ...(toolTip.state === ToolTipType.Alert
              ? {
                  backgroundColor: "#282D36",
                  border: "1px solid rgba(255,255,255,0.1)",
                  cursor: "cursor-default !important",
                }
              : {
                  backgroundColor: "#282D36",
                  border: "1px solid rgba(255,255,255,0.1)",
                  cursor: "cursor-default !important",
                }),
            ...(toolTip.state === ToolTipType.Warn
              ? {
                  backgroundColor: "#282D36",
                  border: "1px solid white",
                  cursor: "cursor-default !important",
                }
              : {}),
            ...dynamicToolTipCss,
          }}
        >
          {toolTip.value}
        </div>
      )
    }
  }
}

export type OrientationInstruction = {
  direction: ToolTipHangDirection
  targetWidth: number
  targetHeight: number
}

export enum ToolTipHangDirection {
  Above,
  Below,
  Left,
  Right,
  AboveStart,
  BelowStart,
  LeftStart,
  RightStart,
  AboveEnd,
  BelowEnd,
  LeftEnd,
  RightEnd,
  Center,
}

const setStyleType = (hangInstruction: OrientationInstruction): any => {
  switch (hangInstruction.direction) {
    case ToolTipHangDirection.Right: {
      return {
        left: `calc(100% + 5px)`,
        transform: `translateY(-50%)`,
        textAlign: "center",
      }
    }
    case ToolTipHangDirection.Left: {
      return {
        top: `${hangInstruction.targetHeight / 2}px`,
        textAlign: "center",
        transform: `translateY(-50%)`,
        right: `${hangInstruction.targetWidth}px`,
      }
    }
    case ToolTipHangDirection.Above: {
      return {
        left: `50%`,
        textAlign: "left",
        top: `-5px`,
        transform: `translate(-50%, -100%)`,
      }
    }
    case ToolTipHangDirection.Below: {
      return {
        left: `${hangInstruction.targetWidth / 2}px`,
        textAlign: "center",
        transform: `translateX(-50%)`,
        top: `${hangInstruction.targetHeight}px`,
      }
    }
    case ToolTipHangDirection.RightEnd: {
      return {
        bottom: `0px`,
        textAlign: "right",
        left: `${hangInstruction.targetWidth}px`,
      }
    }
    case ToolTipHangDirection.LeftEnd: {
      return {
        bottom: `0px`,
        textAlign: "right",
        right: `${hangInstruction.targetWidth}px`,
      }
    }
    case ToolTipHangDirection.AboveEnd: {
      return {
        right: `0%`,
        textAlign: "left",
        top: `-5px`,
        transform: `translate(0, -100%)`,
      }
    }
    case ToolTipHangDirection.BelowEnd: {
      return {
        right: `0px`,
        textAlign: "right",
        top: `${hangInstruction.targetHeight + 5}px`,
      }
    }
    case ToolTipHangDirection.RightStart: {
      return {
        top: `0px`,
        textAlign: "left",
        left: `${hangInstruction.targetWidth}px`,
      }
    }
    case ToolTipHangDirection.LeftStart: {
      return {
        top: `0px`,
        textAlign: "left",
        right: `${hangInstruction.targetWidth}px`,
      }
    }
    case ToolTipHangDirection.AboveStart: {
      return {
        left: `0%`,
        textAlign: "left",
        top: `-5px`,
        transform: `translate(0%, -100%)`,
      }
    }
    case ToolTipHangDirection.BelowStart: {
      return {
        top: `${hangInstruction.targetHeight + 5}px`,
        textAlign: "left",
        left: `0px`,
      }
    }
    case ToolTipHangDirection.Center: {
      return {
        left: `50%`,
        textAlign: "center",
        top: `50%`,
        transform: `translate(-50%, -50%)`,
      }
    }
  }
}

export enum ToolTipType {
  Standard,
  Alert,
  Warn,
  Headless,
}

type StandardToolTip = {
  state: ToolTipType.Standard
  value: string | ReactNode
  width?: string
  height?: string
}

type AlertToolTip = {
  state: ToolTipType.Alert
  value: string
  width?: string
  height?: string
}

type WarnToolTip = {
  state: ToolTipType.Warn
  value: string
  width?: string
  height?: string
}

type HeadlessToolTip = {
  state: ToolTipType.Headless
  value: ReactElement
  className: string
}

type ToolTip = StandardToolTip | AlertToolTip | WarnToolTip | HeadlessToolTip
export enum DisplayBehavior {
  Hover,
  Timer,
}

export const ToolTipWrapper: FC<
  DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
    disabled?: boolean
    toolTip: ToolTip
    direction?: ToolTipHangDirection
    displayBehavior?: DisplayBehavior
  }
> = ({
  disabled,
  toolTip,
  direction = ToolTipHangDirection.AboveEnd,
  displayBehavior = DisplayBehavior.Timer,
  children,
  ...outerProps
}) => {
  const toolTipWrapper = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState<boolean>(false)
  const [_showing, setShowing] = useState<boolean>(displayBehavior === DisplayBehavior.Timer)
  let displayTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  // Only actually show it if it's not disabled
  const showing = _showing && !disabled

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (displayBehavior === DisplayBehavior.Timer) {
      // @ts-ignore
      displayTimer.current = setTimeout(() => {
        setShowing(false)
      }, 3000)
    } else {
    }
  }, [setShowing, displayBehavior])

  let hangInstruction = useMemo<OrientationInstruction | undefined>(() => {
    if (
      mounted &&
      toolTipWrapper.current &&
      toolTipWrapper.current.clientHeight &&
      toolTipWrapper.current.clientWidth
    ) {
      return {
        direction: direction,
        targetHeight: toolTipWrapper.current.clientHeight,
        targetWidth: toolTipWrapper.current.clientWidth,
      }
    } else {
      return undefined
    }
  }, [toolTipWrapper, direction, mounted])

  return (
    <div
      ref={toolTipWrapper}
      onMouseEnter={() => {
        // @ts-ignore
        displayTimer.current = setTimeout(() => {
          setShowing(true)
        }, 350)
      }}
      onMouseLeave={() => {
        if (displayTimer.current) clearTimeout(displayTimer.current)
        setShowing(false)
      }}
      {...outerProps}
    >
      {children}
      {hangInstruction && showing ? (
        <Transition
          show={showing}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <ToolTip orientationInstruction={hangInstruction} toolTip={toolTip} />
        </Transition>
      ) : undefined}
    </div>
  )
}

const defaultToastOptions: ToastOptions = {
  theme: "dark" as Theme,
  position: "top-right" as ToastPosition,
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  closeOnClick: true,
  draggable: true,
  draggablePercent: 40,
  draggableDirection: "x" as DraggableDirection,
}

export const toast = {
  loading: (content: ToastContent): React.ReactText => {
    return toastBase.loading(content, defaultToastOptions)
  },
  success: ({
    content,
    options,
    toastId,
  }: {
    content: ToastContent
    options?: UpdateOptions
    toastId?: Id
  }): React.ReactText => {
    if (toastId) {
      toastBase.update(toastId, {
        render: content,
        type: toastBase.TYPE.SUCCESS,
        isLoading: false,
        icon: <IoCheckmarkCircle className={` w-10 text-accent-light`} />,
        ...defaultToastOptions,
      })
      return toastId
    } else {
      return toastBase.success(content, {
        icon: <IoCheckmarkCircle className={` w-10 text-accent-light`} />,
        ...defaultToastOptions,
      })
    }
  },
  error: ({
    content,
    options,
    toastId,
  }: {
    content: ToastContent
    options?: UpdateOptions
    toastId?: Id
  }): React.ReactText => {
    if (toastId) {
      toastBase.update(toastId, {
        render: content,
        type: toastBase.TYPE.ERROR,
        isLoading: false,
        icon: <IoAlertCircle className={`w-10 text-alert-primary`} />,
        ...defaultToastOptions,
      })
      return toastId
    } else {
      return toastBase.error(content, {
        icon: <IoAlertCircle className={`w-10 text-alert-primary`} />,
        ...defaultToastOptions,
      })
    }
  },
  dismiss: (toastId: Id) => {
    toastBase.dismiss(toastId)
  },
}
